<template>
  <div class="occupancy-calendar">
    <!-- Non-modal on purpose: the embedded calendar (Mobiscroll) portals its
         filter/search popups to <body>, outside the dialog content. A modal
         dialog locks body pointer events and traps focus, which makes those
         popups unclickable. The dialog is full-screen and already prevents
         every outside-dismiss interaction, so the modal lock buys nothing. -->
    <SDialog v-model:open="dialogVisible" :modal="false">
      <SDialogContent
        :show-close-button="false"
        data-cy="occupancy-calendar-dialog"
        class="occupancy-calendar !fixed !inset-0 !top-0 !left-0 !w-screen !h-screen !max-w-none !rounded-none !translate-x-0 !translate-y-0 !p-0 !border-0 !gap-0 !grid-rows-[auto_auto_1fr] overflow-y-auto overflow-x-hidden [&>*]:min-w-0"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
        @interact-outside.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("occupancy-calendar.dialog-header") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="customized-header d-flex flex-row fl-s-b">
          <div class="header-title d-flex fl-col fl-j-c">
            <p>{{ $t("occupancy-calendar.dialog-header") }}</p>
          </div>
          <div
            class="close-button d-flex fl-col fl-j-c"
            data-cy="occupancy-calendar-close"
            @click="close"
          >
            <SvgIcon
              :iconContent="closeIconContent"
              :iconAlt="closeIconAlt"
              :iconStyle="closeIconStyle"
            />
          </div>
        </div>
        <div class="customized-body">
          <NoAccessEmpty
            v-if="!getHasAccess"
            data-cy="occupancy-calendar-no-access"
          />
          <smeetz-calendars
            v-else-if="showCalendar && dialogVisible"
            :userId="getUserId"
            :userGraphqlToken="getUserGraphqlToken"
            :userTableKey="getOccupancyTableKey"
            :searchServiceType="2"
            :userToken="getUserToken"
            :groupId="String(getGroupId)"
            :env="env"
            :graphqlUrl="graphqlUrl"
            :rootApiUrl="rootApiUrl"
            :servicesUrl="servicesUrl"
            :apiKey="apiKey"
            :lang="getLanguage()"
            :filters="[
              { key: 'group_access', option: 12, value: Number(getGroupId) },
            ]"
            :features="calendarFeatures"
            height="92vh"
            appType="pos"
            @edit-button:clicked="onCalendar($event)"
          />
          <div
            v-else
            class="d-flex fl-j-c fl-a-c h-full"
            data-cy="occupancy-calendar-loading"
          >
            <Spinner />
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import ModalModule from "@/store/modules/modal";
import closeIcon from "@/assets/images/svg/close.svg";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import NoAccessEmpty from "@/components/common/NoAccessEmpty.vue";
import AuthModule from "@/store/modules/auth";
import { NetworkErrors } from "@/constants/errors/network";
import {
  getUserAccessToken,
  getSmtzAuth,
  getSmtzAuthFromStorage,
  showSessionExpiredPopup,
} from "@/utils/helpers/auth";
import { getLanguage } from "@/utils/helpers/lang";
import { openOrderFromTable } from "@/utils/vue-helpers/orders";
import {
  getCalendarPackageFeatures,
  waitForLiveFlags,
  CalendarPackageFeatures,
} from "@/utils/helpers/feature-flags";
import { Spinner } from "@/components/index";

// @ts-ignore
import { SmeetzCalendars } from "/node_modules/@smeetz/calendars/dist/calendars.js";
import "/node_modules/@smeetz/calendars/dist/style.css";

import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "OccupancyCalendar",
  components: {
    SmeetzCalendars,
    Spinner,
    NoAccessEmpty,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  async setup() {
    const { t } = useI18n({});
    const closeIconStyle =
      "width: 30px; height: 30px; filter: contrast(170%) invert(100%); cursor: pointer;";
    const closeIconContent = closeIcon;
    const closeIconAlt = "Close";
    const showCalendar = ref<boolean>(false);

    // The occupancy engine is granted to manager-level roles only. A seller
    // gets a null token, so the calendar shows the no-access panel instead.
    const getHasAccess = computed(
      () => !!UserModule.getUser?.elasticSearchTokens?.occupancy
    );

    onMounted(async () => {
      if (!getHasAccess.value) {
        return;
      }
      // The embedded calendar authenticates its own GraphQL calls with the
      // smtz-auth token we pass as a prop and does not refresh it itself, so we make
      // sure we hold a valid one before mounting. getSmtzAuth() is the single
      // source of truth: it returns the cached token or transparently refresh an
      // expired one.
      try {
        // The widget copies its features prop once at mount, so give
        // pos::package:calendar a moment to be answered on a boot where
        // Flagsmith is late, else the calendar would open on the legacy path.
        // Independent of the token check, so the two waits overlap.
        const [, flagsAreLive] = await Promise.all([
          getSmtzAuth(),
          waitForLiveFlags(),
        ]);
        calendarFeatures.value = getCalendarPackageFeatures();
        // The flags can still arrive while a slow token check settles, so go
        // by what was read rather than by the wait alone.
        if (!flagsAreLive && !calendarFeatures.value["calendar-v2"]) {
          LogService.info(
            "Occupancy calendar opened without a live flag answer, so calendar-v2 is off",
            { groupId: POSConfigModule.groupId }
          );
        }
        showCalendar.value = true;
      } catch (error) {
        LogService.info(
          "Could not obtain a valid smtz auth token to open the occupancy calendar",
          { error },
          LogLevel.Error
        );
        if (
          error instanceof Error &&
          error.message === NetworkErrors.NoAccessToken
        ) {
          // No session at all: route the user to re-login.
          showSessionExpiredPopup();
        } else if (!AuthModule.showingSessionExpiryPopup) {
          // A 401/403 while re-minting already triggered the session-expired flow
          // through the shared request interceptor; for anything else (offline,
          // backend issue) show the classified message and stay signed in.
          NotificationService.showCustomMessage(
            handleErrorAndPrepareMessage(error).errorMessage,
            "error"
          );
        }
      }
    });

    const dialogVisible = dialogVisibleForStatus(ModalStatus.OccupancyCalendar);
    // Widget features from pos::package:calendar, captured once the flags are
    // live (or the wait ran out) so a later refresh does not change the prop
    // under a mounted widget.
    const calendarFeatures = ref<CalendarPackageFeatures>({});
    const getOccupancyTableKey = computed(() => {
      return UserModule.getUser?.elasticSearchTokens?.occupancy;
    });
    const getUserToken = computed(() => {
      return AuthModule.token || getUserAccessToken();
    });
    const getGroupId = computed(() => {
      const groupId = String(POSConfigModule.groupId);

      return groupId;
    });
    const getUserId = computed(() => {
      return String(UserModule.userId);
    });

    const getUserGraphqlToken = computed(() => {
      return getSmtzAuthFromStorage();
    });
    const env = import.meta.env.VITE_ENV;

    // The calendars package falls back to its bundled dev/test/prod defaults for
    // anything it is not given, which sends every other environment - sandbox
    // included - to dev. This deployment already knows its own endpoints, so it
    // hands them over and nothing is derived from the environment name.
    const graphqlUrl = import.meta.env.VITE_GQL_API;
    const servicesUrl = import.meta.env.VITE_SERVICES_API;
    const apiKey = import.meta.env.VITE_X_API_KEY;
    // The widget wants the language segment on the end, the way its own default
    // builds …/b2b2/en. VITE_ROOT_API's shape differs per deployment - prod carries
    // https://api.smeetz.com/b2b2/prod/, test https://api.test.smeetz.com/b2b2/ - and
    // every value checked ends in a slash, so this is a no-op today. It removes the
    // assumption rather than fixing a break: the shape already varies, and a value
    // saved without the slash would build …/b2b2en, a missing one "undefined".
    const rootApi = (import.meta.env.VITE_ROOT_API ?? "").replace(/\/+$/, "");
    const rootApiUrl = `${rootApi}/${getLanguage()}`;

    /**
     * Opens the order behind a clicked calendar slot through the same flow as
     * the Orders table / QR scan (`openOrderFromTable`): already-open orders
     * are only brought into view, everything else is fetched by reference,
     * and the modal closes on success.
     */
    async function onCalendar(value: any) {
      const reference = value?._rawData?.occupancy_booking_reference?.raw;
      if (!reference) {
        LogService.info(
          `Occupancy calendar: clicked slot carries no order reference, occupancyBookingId: ${value?.occupancyBookingId}`,
          { params: value?._rawData },
          LogLevel.Error
        );
        NotificationService.showGeneralResponseError();
        return;
      }

      try {
        await openOrderFromTable({ reference });
      } catch (err: any) {
        const errorDetails = handleErrorAndPrepareMessage(err);
        LogService.info(
          `${errorDetails.errorType} Error opening order ${reference} from the occupancy calendar`,
          { params: { err, errorDetails } },
          LogLevel.Error
        );
        NotificationService.showCustomMessage(
          errorDetails.errorMessage,
          "error"
        );
      }
    }

    return {
      t,
      dialogVisible,
      onCalendar,
      calendarFeatures,
      getOccupancyTableKey,
      getUserToken,
      getGroupId,
      getLanguage,
      getUserGraphqlToken,
      getUserId,
      showCalendar,
      getHasAccess,
      env,
      graphqlUrl,
      rootApiUrl,
      servicesUrl,
      apiKey,
      closeIconStyle,
      closeIconContent,
      closeIconAlt,
    };
  },
  methods: {
    close() {
      ModalModule.setStatus(ModalStatus.Hidden);
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.occupancy-calendar {
  .customized-header {
    padding-left: 30px;
    padding-right: 30px;
    background-color: #004c53;
    height: 45px;
    color: white;
    position: sticky;
    top: 0;
    z-index: 2005; // Greater than element-plus dialog z-index value which is 2003

    .header-title p {
      font-size: 18px;
      font-weight: 700;
    }

    .go-back-icon {
      margin-right: 15px;
    }
  }

  .customized-body {
    .order-details {
      height: 100%;
      .progressbar-wrapper {
        height: 125px;
        width: 40%;
        margin-left: auto;
        margin-right: auto;
        margin-top: 40px;
        margin-bottom: 40px;
        text-align: center;
        color: #004c53;

        .img-wrapper {
          img {
            width: 200px;
            height: 137px;
          }
        }

        .loading-text {
          font-weight: 600;
          font-size: 17px;
          line-height: 19px;
          margin-bottom: 10px;
        }

        .one-sec-text {
          font-weight: 400;
          font-size: 14px;
          line-height: 16px;
          margin-top: 15px;
        }

        .el-progress-bar {
          .el-progress-bar__outer {
            height: 26px;
            border: 2px solid #004c53;
            background-color: transparent;

            .el-progress-bar__inner {
              height: 20px;
              top: 1px;
            }
          }
        }
      }
    }
  }

  .el-table__body {
    border-collapse: separate;
    border-spacing: 0 5px;
  }
  thead .el-table__cell {
    background-color: rgba(48, 170, 188, 0.1);
    color: #333333;

    .cell {
      word-break: break-word;
    }
  }

  .filters-wrapper {
    // width: fit-content;
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;
    margin-bottom: 20px;

    .filters-row {
      margin-top: 5px;
      margin-bottom: 5px;

      .sort-title {
        font-weight: 600;
        font-style: normal;
        font-size: 15px;
        text-align: center;
        line-height: 15px;
        margin-right: 10px;
      }

      .filter-button {
        width: 160px;
        height: 36px;
        font-weight: 500;
        font-style: normal;
        font-size: 14px;
        text-align: center;
        line-height: 15px;
        letter-spacing: 0px;
        margin-left: 5px;
        margin-right: 5px;
      }

      .search-input {
        border: 1px solid #dcdfe6;
        color: #606266;
        letter-spacing: 1px;
        width: 250px;
        border-radius: 4px;
      }
      .search-input:focus {
        border: 1px solid #dcdfe6;
        outline: none;
      }
      .search-input::placeholder {
        color: #dcdfe6;
        text-align: left;
        padding-left: 5px;
      }
      .search-input::-webkit-search-cancel-button {
        opacity: 0.4;
      }
    }

    .active {
      background-color: $color-pacific-blue;
      font-weight: bold;
      color: $background-color-base;
    }
  }

  .orders-table-wrapper {
    padding-top: 15px;

    .el-table-fixed-column--right {
      background-color: transparent;
    }

    .edit-order-btn {
      border: 1px solid rgb(217, 223, 229);
      padding-left: 6px;
      padding-right: 6px;
      border-radius: 4px;
      background-color: rgb(253, 254, 254);

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        height: 32px;
        line-height: 32px;
        text-align: center;
      }
    }
  }

  .link {
    text-decoration: underline;
    cursor: pointer;
    color: $color-dark-cyan;
  }
}
</style>
